import { useMemo, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Snackbar, Alert,
} from '@mui/material'
import {
  Add, MoreVert, Edit, PlayArrow, Pause, RestartAlt, Cancel, Inventory2, NotStarted, Assignment,
  ReportProblem, DoneAll, Lock, QueryStats, Search, AccountTree,
} from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listProductionOrders, suspendProductionOrder, reactivateProductionOrder, cancelProductionOrder,
  startExecution, finalizePartial, closeProductionOrder,
  type ProductionOrderListItem, type ProductionOrderStatus, type ProductionPriority,
} from '../../api/productieOrdine'
import { errorMessage } from '../../api/client'
import OrdineFormDialog from './OrdineFormDialog'
import LaunchOrderDialog from './LaunchOrderDialog'
import MaterialAllocationDialog from './MaterialAllocationDialog'
import ReportProductionDialog from './ReportProductionDialog'
import OperationRunsDialog from './OperationRunsDialog'
import DowntimeDialog from './DowntimeDialog'
import OeeDialog from './OeeDialog'
import OrderTraceabilityDialog from './OrderTraceabilityDialog'

const STATUS_LABEL: Record<ProductionOrderStatus, string> = {
  Planificat: 'Planificat', Lansat: 'Lansat', InPregatire: 'În pregătire', InExecutie: 'În execuție',
  PartialFinalizat: 'Parțial finalizat', Finalizat: 'Finalizat', Inchis: 'Închis', Suspendat: 'Suspendat', Anulat: 'Anulat',
}
const STATUS_COLOR: Record<ProductionOrderStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  Planificat: 'default', Lansat: 'info', InPregatire: 'info', InExecutie: 'info', PartialFinalizat: 'warning',
  Finalizat: 'success', Inchis: 'success', Suspendat: 'warning', Anulat: 'error',
}
const PRIORITY_LABEL: Record<ProductionPriority, string> = { Scazuta: 'Scăzută', Normala: 'Normală', Ridicata: 'Ridicată', Urgenta: 'Urgentă' }

export default function OrdinePage() {
  const [formTarget, setFormTarget] = useState<ProductionOrderListItem | null | 'new'>(null)
  const [launchTarget, setLaunchTarget] = useState<ProductionOrderListItem | null>(null)
  const [allocationTarget, setAllocationTarget] = useState<ProductionOrderListItem | null>(null)
  const [reportTarget, setReportTarget] = useState<ProductionOrderListItem | null>(null)
  const [operationsTarget, setOperationsTarget] = useState<ProductionOrderListItem | null>(null)
  const [downtimeTarget, setDowntimeTarget] = useState<ProductionOrderListItem | null>(null)
  const [oeeTarget, setOeeTarget] = useState<ProductionOrderListItem | null>(null)
  const [traceabilityOpen, setTraceabilityOpen] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: ProductionOrderListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['productie', 'ordine'], queryFn: listProductionOrders })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['productie', 'ordine'] })

  const suspendMutation = useMutation({ mutationFn: suspendProductionOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const reactivateMutation = useMutation({ mutationFn: reactivateProductionOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const cancelMutation = useMutation({ mutationFn: cancelProductionOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const startExecutionMutation = useMutation({ mutationFn: startExecution, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const finalizePartialMutation = useMutation({ mutationFn: finalizePartial, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const closeMutation = useMutation({ mutationFn: closeProductionOrder, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })

  const columns = useMemo<GridColDef<ProductionOrderListItem>[]>(() => [
    { field: 'number', headerName: 'Nr.', width: 130, renderCell: (p) => <code>{p.row.number}</code> },
    { field: 'productArticleDescription', headerName: 'Produs', flex: 1, minWidth: 200,
      renderCell: (p) => `${p.row.productArticleCode} — ${p.row.productArticleDescription}` },
    { field: 'plannedQuantityThousands', headerName: 'Cant. (mii)', width: 110, type: 'number' },
    { field: 'equipmentName', headerName: 'Utilaj', width: 160 },
    { field: 'plannedStartDate', headerName: 'Start', width: 110 },
    { field: 'plannedEndDate', headerName: 'Final', width: 110 },
    { field: 'priority', headerName: 'Prioritate', width: 110, valueGetter: (v: ProductionPriority) => PRIORITY_LABEL[v] },
    { field: 'status', headerName: 'Status', width: 140, sortable: false, renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" /> },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Producție</Typography>
        <Typography variant="inherit" color="text.primary">Ordine de producție</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Ordine de producție</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" startIcon={<Search />} onClick={() => setTraceabilityOpen(true)}>Trasabilitate lot</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Ordin nou</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <OrdineFormDialog target={formTarget} onClose={() => setFormTarget(null)} />
      <LaunchOrderDialog target={launchTarget} onClose={() => setLaunchTarget(null)} onLaunched={invalidate} />
      <MaterialAllocationDialog target={allocationTarget} onClose={() => setAllocationTarget(null)} />
      <ReportProductionDialog target={reportTarget} onClose={() => setReportTarget(null)} />
      <OperationRunsDialog target={operationsTarget} onClose={() => setOperationsTarget(null)} />
      <DowntimeDialog target={downtimeTarget} onClose={() => setDowntimeTarget(null)} />
      <OeeDialog target={oeeTarget} onClose={() => setOeeTarget(null)} />
      <OrderTraceabilityDialog open={traceabilityOpen} onClose={() => setTraceabilityOpen(false)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={menuAnchor.row.status !== 'Planificat'} onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="launch" disabled={menuAnchor.row.status !== 'Planificat'} onClick={() => { setLaunchTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><PlayArrow fontSize="small" /></ListItemIcon><ListItemText>Lansează</ListItemText>
          </MenuItem>,
          <MenuItem key="allocation" disabled={menuAnchor.row.status === 'Planificat'} onClick={() => { setAllocationTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Inventory2 fontSize="small" /></ListItemIcon><ListItemText>Alocare materiale</ListItemText>
          </MenuItem>,
          <MenuItem key="start" disabled={menuAnchor.row.status !== 'Lansat'} onClick={() => { startExecutionMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><NotStarted fontSize="small" /></ListItemIcon><ListItemText>Începe execuția</ListItemText>
          </MenuItem>,
          <MenuItem key="report" disabled={menuAnchor.row.status !== 'InExecutie' || menuAnchor.row.hasRoute}
            onClick={() => { setReportTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Assignment fontSize="small" /></ListItemIcon><ListItemText>Raportează producție</ListItemText>
          </MenuItem>,
          <MenuItem key="operations" disabled={!menuAnchor.row.hasRoute || menuAnchor.row.status === 'Planificat'}
            onClick={() => { setOperationsTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><AccountTree fontSize="small" /></ListItemIcon><ListItemText>Operații</ListItemText>
          </MenuItem>,
          <MenuItem key="downtime" disabled={!['Lansat', 'InExecutie'].includes(menuAnchor.row.status)} onClick={() => { setDowntimeTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><ReportProblem fontSize="small" /></ListItemIcon><ListItemText>Opriri</ListItemText>
          </MenuItem>,
          <MenuItem key="oee" onClick={() => { setOeeTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><QueryStats fontSize="small" /></ListItemIcon><ListItemText>OEE</ListItemText>
          </MenuItem>,
          <MenuItem key="suspend" disabled={!['Lansat', 'InExecutie'].includes(menuAnchor.row.status)} onClick={() => { suspendMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Pause fontSize="small" /></ListItemIcon><ListItemText>Suspendă</ListItemText>
          </MenuItem>,
          <MenuItem key="reactivate" disabled={menuAnchor.row.status !== 'Suspendat'} onClick={() => { reactivateMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><RestartAlt fontSize="small" /></ListItemIcon><ListItemText>Reactivează</ListItemText>
          </MenuItem>,
          <MenuItem key="finalizePartial" disabled={menuAnchor.row.status !== 'InExecutie'} onClick={() => { finalizePartialMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><DoneAll fontSize="small" /></ListItemIcon><ListItemText>Finalizează parțial</ListItemText>
          </MenuItem>,
          <MenuItem key="close" disabled={!['Finalizat', 'PartialFinalizat'].includes(menuAnchor.row.status)} onClick={() => { closeMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Lock fontSize="small" /></ListItemIcon><ListItemText>Închide</ListItemText>
          </MenuItem>,
          <MenuItem key="cancel" disabled={['Inchis', 'Anulat', 'Finalizat'].includes(menuAnchor.row.status)}
            onClick={() => { cancelMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Cancel fontSize="small" /></ListItemIcon><ListItemText>Anulează</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
